import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Input,
    NumberInput,
    Spinner,
} from "@heroui/react";
import * as turf from "@turf/turf";
import { Feature, MultiPolygon, Polygon } from "geojson";
import { MapLayerMouseEvent } from "mapbox-gl";
import {
    type Key,
    type KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    PLSS_TILES_FILL_LAYER,
    PLSS_TILES_LABEL_LAYER,
    PLSS_TILES_LINE_LAYER,
    PLSS_TILES_SOURCE,
} from "@/lib/map/layers";
import useMap from "@/lib/map/useMap";
import { getApiBaseUrl } from "@/lib/request/RequestProvider";
import useRequest from "@/lib/request/useRequest";
import {
    buildPlssTilesUrl,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";

type PlssFeature = Feature<Polygon | MultiPolygon>;
type PlssBounds = [number, number, number, number];

interface PlssBoundarySelectorProps {
    onSelect: (feature: PlssFeature) => void;
}

interface PlssSearchResult {
    source_id: string;
    meridian: string;
    bounds: PlssBounds;
}

interface PlssSearchResponse {
    results: PlssSearchResult[];
}

const PLSS_STATES = [
    ["AL", "Alabama"],
    ["AR", "Arkansas"],
    ["AZ", "Arizona"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["FL", "Florida"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["IA", "Iowa"],
    ["KS", "Kansas"],
    ["LA", "Louisiana"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MO", "Missouri"],
    ["MS", "Mississippi"],
    ["MT", "Montana"],
    ["ND", "North Dakota"],
    ["NE", "Nebraska"],
    ["NM", "New Mexico"],
    ["NV", "Nevada"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["SD", "South Dakota"],
    ["UT", "Utah"],
    ["WA", "Washington"],
    ["WI", "Wisconsin"],
    ["WY", "Wyoming"],
] as const;

function normalizeSection(value: string): string | null {
    if (!/^\d+$/.test(value)) {
        return null;
    }
    const section = Number(value);
    return section >= 1 && section <= 36 ? String(section) : null;
}

function normalizeTownship(value: string): string | null {
    const match = /^([1-9]\d{0,2})([NS])$/i.exec(value.trim());
    return match ? `${Number(match[1])}${match[2].toUpperCase()}` : null;
}

function normalizeRange(value: string): string | null {
    const match = /^([1-9]\d{0,2})([EW])$/i.exec(value.trim());
    return match ? `${Number(match[1])}${match[2].toUpperCase()}` : null;
}

export default function PlssBoundarySelector({
    onSelect,
}: PlssBoundarySelectorProps) {
    const { map, mapLoaded } = useMap();
    const { axiosRequest } = useRequest();
    const [stateCode, setStateCode] = useState("");
    const [section, setSection] = useState("");
    const [township, setTownship] = useState("");
    const [range, setRange] = useState("");
    const [results, setResults] = useState<PlssSearchResult[]>([]);
    const [error, setError] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false);
    const requestAbortController = useRef<AbortController | null>(null);
    const tilesUrl = buildPlssTilesUrl(getApiBaseUrl());

    const clearActiveWork = useCallback(
        (updateState = true) => {
            requestAbortController.current?.abort();
            requestAbortController.current = null;
            if (updateState) {
                setIsSearching(false);
                setIsSelecting(false);
            }
        },
        [],
    );

    const resetResults = useCallback(() => {
        clearActiveWork();
        setResults([]);
        setError("");
    }, [clearActiveWork]);

    const selectCandidate = useCallback(
        (result: PlssSearchResult) => {
            if (!map || !mapLoaded) {
                setError("The map is not available.");
                setIsSelecting(false);
                return;
            }

            clearActiveWork();
            setError("");
            try {
                const [west, south, east, north] = result.bounds;
                if (west >= east || south >= north) {
                    throw new Error("PLSS boundary has invalid bounds.");
                }
                onSelect(
                    turf.bboxPolygon(result.bounds, {
                        id: result.source_id,
                        properties: { source_feature_id: result.source_id },
                    }),
                );
            } catch {
                setError("That PLSS boundary could not be selected.");
            }
        },
        [clearActiveWork, map, mapLoaded, onSelect],
    );

    const handleMapClick = useCallback(
        (event: MapLayerMouseEvent) => {
            const sourceId =
                event.features?.[0]?.properties?.source_feature_id;
            if (typeof sourceId !== "string") {
                return;
            }

            clearActiveWork();
            const controller = new AbortController();
            requestAbortController.current = controller;
            setError("");
            setIsSelecting(true);
            void axiosRequest<PlssSearchResponse>({
                url: "plss/search/",
                method: "get",
                params: { source_id: sourceId },
                signal: controller.signal,
            })
                .then((response) => {
                    if (controller.signal.aborted) {
                        return;
                    }
                    const result = response.results.find(
                        (candidate) => candidate.source_id === sourceId,
                    );
                    if (!result) {
                        throw new Error("Boundary not found");
                    }
                    if (requestAbortController.current === controller) {
                        requestAbortController.current = null;
                    }
                    selectCandidate(result);
                })
                .catch(() => {
                    if (!controller.signal.aborted) {
                        clearActiveWork();
                        setError("That PLSS boundary could not be selected.");
                    }
                })
                .finally(() => {
                    if (requestAbortController.current === controller) {
                        requestAbortController.current = null;
                        setIsSelecting(false);
                    }
                });
        },
        [axiosRequest, clearActiveWork, selectCandidate],
    );

    useEffect(() => {
        if (!map || !mapLoaded) {
            return;
        }

        if (!map.getSource(PLSS_TILES_SOURCE)) {
            map.addSource(PLSS_TILES_SOURCE, {
                type: "vector",
                tiles: [tilesUrl],
                minzoom: 8,
                maxzoom: 14,
            });
        }
        if (!map.getLayer(PLSS_TILES_FILL_LAYER.id)) {
            map.addLayer(PLSS_TILES_FILL_LAYER);
        }
        if (!map.getLayer(PLSS_TILES_LINE_LAYER.id)) {
            map.addLayer(PLSS_TILES_LINE_LAYER);
        }
        if (!map.getLayer(PLSS_TILES_LABEL_LAYER.id)) {
            map.addLayer(PLSS_TILES_LABEL_LAYER);
        }

        const showPointer = () => {
            map.getCanvas().style.cursor = "pointer";
        };
        const clearPointer = () => {
            map.getCanvas().style.cursor = "";
        };
        map.on("click", PLSS_TILES_FILL_LAYER.id, handleMapClick);
        map.on("mouseenter", PLSS_TILES_FILL_LAYER.id, showPointer);
        map.on("mouseleave", PLSS_TILES_FILL_LAYER.id, clearPointer);

        return () => {
            map.off("click", PLSS_TILES_FILL_LAYER.id, handleMapClick);
            map.off("mouseenter", PLSS_TILES_FILL_LAYER.id, showPointer);
            map.off("mouseleave", PLSS_TILES_FILL_LAYER.id, clearPointer);
            clearPointer();
            removeLayersIfExists(map, [
                PLSS_TILES_LABEL_LAYER.id,
                PLSS_TILES_LINE_LAYER.id,
                PLSS_TILES_FILL_LAYER.id,
            ]);
            removeSourcesIfExists(map, [PLSS_TILES_SOURCE]);
        };
    }, [handleMapClick, map, mapLoaded, tilesUrl]);

    useEffect(() => {
        return () => {
            clearActiveWork(false);
        };
    }, [clearActiveWork]);

    const handleSearch = useCallback(async () => {
        const normalizedSection = normalizeSection(section);
        const normalizedTownship = normalizeTownship(township);
        const normalizedRange = normalizeRange(range);
        if (
            !stateCode ||
            !normalizedSection ||
            !normalizedTownship ||
            !normalizedRange
        ) {
            setError(
                "Choose a state and enter a valid section, township, and range.",
            );
            setResults([]);
            return;
        }

        clearActiveWork();
        const controller = new AbortController();
        requestAbortController.current = controller;
        setSection(normalizedSection);
        setTownship(normalizedTownship);
        setRange(normalizedRange);
        setResults([]);
        setError("");
        setIsSearching(true);

        try {
            const response = await axiosRequest<PlssSearchResponse>({
                url: "plss/search/",
                method: "get",
                params: {
                    state: stateCode,
                    section: normalizedSection,
                    township: normalizedTownship,
                    range: normalizedRange,
                },
                signal: controller.signal,
            });
            if (controller.signal.aborted) {
                return;
            }
            setResults(response.results);
            if (response.results.length === 0) {
                setError("No matching PLSS boundaries were found.");
            }
        } catch {
            if (!controller.signal.aborted) {
                setError("The PLSS search could not be completed.");
            }
        } finally {
            if (requestAbortController.current === controller) {
                requestAbortController.current = null;
                setIsSearching(false);
            }
        }
    }, [
        axiosRequest,
        clearActiveWork,
        range,
        section,
        stateCode,
        township,
    ]);

    const updateState = (key: Key | null) => {
        resetResults();
        setStateCode(typeof key === "string" ? key : "");
    };
    const isBusy = isSearching || isSelecting;
    const handleSearchKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && !isBusy) {
            event.preventDefault();
            void handleSearch();
        }
    };

    return (
        <div className="flex flex-col gap-3" aria-busy={isBusy}>
            <p className="text-sm text-default-500">
                Search by PLSS description or click a section outline on the
                map.
            </p>
            <Autocomplete
                label="State"
                placeholder="Select a state"
                selectedKey={stateCode || null}
                allowsCustomValue={false}
                isDisabled={isBusy}
                onSelectionChange={updateState}
            >
                {PLSS_STATES.map(([code, name]) => (
                    <AutocompleteItem
                        key={code}
                        textValue={`${name} ${code}`}
                    >
                        {name} ({code})
                    </AutocompleteItem>
                ))}
            </Autocomplete>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <NumberInput
                    label="Section"
                    placeholder="4"
                    minValue={1}
                    maxValue={36}
                    step={1}
                    value={section ? Number(section) : NaN}
                    isDisabled={isBusy}
                    onKeyDown={handleSearchKeyDown}
                    onValueChange={(value) => {
                        resetResults();
                        setSection(Number.isFinite(value) ? String(value) : "");
                    }}
                />
                <Input
                    label="Township"
                    placeholder="83N"
                    value={township}
                    maxLength={4}
                    isDisabled={isBusy}
                    onKeyDown={handleSearchKeyDown}
                    onValueChange={(value) => {
                        resetResults();
                        setTownship(value);
                    }}
                />
                <Input
                    label="Range"
                    placeholder="24W"
                    value={range}
                    maxLength={4}
                    isDisabled={isBusy}
                    onKeyDown={handleSearchKeyDown}
                    onValueChange={(value) => {
                        resetResults();
                        setRange(value);
                    }}
                />
            </div>
            <Button
                type="button"
                color="primary"
                isLoading={isSearching}
                isDisabled={isBusy}
                onPress={handleSearch}
            >
                Search PLSS
            </Button>
            <div aria-live="polite">
                {error && (
                    <p className="text-sm text-danger" role="alert">
                        {error}
                    </p>
                )}
                {isSelecting && (
                    <div className="flex items-center gap-2 text-sm text-default-500">
                        <Spinner size="sm" />
                        Preparing boundary…
                    </div>
                )}
            </div>
            {results.length > 0 && (
                <div className="flex flex-col gap-2">
                    <p className="text-sm text-default-500" aria-live="polite">
                        {results.length === 1
                            ? "1 boundary found"
                            : `${results.length} boundaries found. Choose a meridian.`}
                    </p>
                    {results.map((result) => (
                        <Button
                            key={result.source_id}
                            type="button"
                            variant="bordered"
                            className="h-auto min-h-16 w-full justify-start border-default-200 bg-content1 px-3 py-2 text-left shadow-small transition-colors hover:border-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                            isDisabled={isBusy}
                            onPress={() => selectCandidate(result)}
                        >
                            <span className="flex w-full min-w-0 flex-col items-start gap-1 text-left">
                                <span className="text-sm font-medium">
                                    Meridian {result.meridian}
                                </span>
                                <span className="break-all text-xs text-default-500">
                                    {result.source_id}
                                </span>
                            </span>
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
}
